import {
  FlatQuestion,
  getQuestionChoices,
  IntegerQuestion,
  OneOfManyQuestion,
  Question,
  QuestionType,
  questionHasChoiceList,
} from '@models/Survey'
import { AnswerObject, AnswerType } from '@models/Survey/Answer'
import { isQuestionVisible } from '@utils/showIf'

import baselineEmployed from './presets/baseline_employed.json'
import q1OnlySmoke from './presets/q1_only_smoke.json'
import retiredMinimal from './presets/retired_minimal.json'

export type Scan26AutofillScope = 'from_zero' | 'remaining_only'

export type Scan26PresetAnswerValue = number | string | { choice_key: string }

export type Scan26PresetDefinition = {
  id: string
  label: string
  description?: string
  answers: Record<string, Scan26PresetAnswerValue>
}

/** Aligné sur `backend/.../SCAN26-E6/presets/` et `manage.py predict_scan26_test`. */
export const SCAN26_DEV_PRESETS: Scan26PresetDefinition[] = [
  baselineEmployed as Scan26PresetDefinition,
  retiredMinimal as Scan26PresetDefinition,
  q1OnlySmoke as Scan26PresetDefinition,
]

export function getScan26PresetById(id: string): Scan26PresetDefinition | undefined {
  return SCAN26_DEV_PRESETS.find((preset) => preset.id === id)
}

type ChoiceLike = { id: string; choiceKey?: string; choice_key?: string }

export function choiceKeyOf(choice: ChoiceLike): string | undefined {
  const key = choice.choiceKey ?? choice.choice_key
  return key != null ? String(key) : undefined
}

export function findChoiceByKey(question: Question, choiceKey: string): ChoiceLike | undefined {
  return getQuestionChoices(question).find((choice) => choiceKeyOf(choice as ChoiceLike) === String(choiceKey))
}

export function resolvePresetValue(question: Question, raw: Scan26PresetAnswerValue): AnswerType | undefined {
  if (typeof raw === 'object' && raw !== null && 'choice_key' in raw) {
    const choice = findChoiceByKey(question, String(raw.choice_key))
    return choice?.id
  }
  if (typeof raw === 'number' || typeof raw === 'string') {
    return raw
  }
  return undefined
}

export function buildAnswersFromPreset(
  questionsList: FlatQuestion[],
  presetAnswers: Record<string, Scan26PresetAnswerValue>,
): AnswerObject {
  const byKey = Object.fromEntries(questionsList.map((question) => [question.key, question]))
  const result: AnswerObject = {}

  for (const [key, raw] of Object.entries(presetAnswers)) {
    const question = byKey[key]
    if (!question) {
      continue
    }
    const resolved = resolvePresetValue(question, raw)
    if (resolved !== undefined) {
      result[question.id] = resolved
    }
  }

  return result
}

function randomInteger(question: IntegerQuestion): number {
  const meta = question.meta ?? {}
  const min = Number(meta.minimum ?? meta.min ?? 18)
  const max = Number(meta.maximum ?? meta.max ?? 99)
  const low = Number.isFinite(min) ? min : 18
  const high = Number.isFinite(max) ? max : 99
  if (high <= low) {
    return low
  }
  return low + Math.floor(Math.random() * (high - low + 1))
}

export function randomAnswerForQuestion(question: FlatQuestion): AnswerType | undefined {
  switch (question.type) {
    case QuestionType.OneOfMany:
    case QuestionType.OneOfManyGraphic: {
      const choices = getQuestionChoices(question as OneOfManyQuestion)
      if (!choices.length) {
        return undefined
      }
      const index = Math.floor(Math.random() * choices.length)
      return choices[index].id
    }
    case QuestionType.FreeText:
      if (questionHasChoiceList(question)) {
        const choices = getQuestionChoices(question)
        if (!choices.length) {
          return undefined
        }
        const index = Math.floor(Math.random() * choices.length)
        return choices[index].id
      }
      return 'DEV'
    case QuestionType.Integer:
      return randomInteger(question as IntegerQuestion)
    case QuestionType.PostalCode:
    case QuestionType.CanadianPostalCode:
      return { code: 'G0A3K0', nmCirc: 'Montmorency' } as unknown as AnswerType
    case QuestionType.Country:
      return {
        code: 'ca',
        artworkUrl: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/4x3/ca.svg',
      } as unknown as AnswerType
    default:
      return undefined
  }
}

export function buildRandomAnswers(questionsList: FlatQuestion[]): AnswerObject {
  const result: AnswerObject = {}
  for (const question of questionsList) {
    const value = randomAnswerForQuestion(question)
    if (value !== undefined) {
      result[question.id] = value
    }
  }
  return result
}

export function mergeAutofillAnswers(
  current: AnswerObject,
  generated: AnswerObject,
  questionsList: FlatQuestion[],
  scope: Scan26AutofillScope,
): AnswerObject {
  if (scope === 'from_zero') {
    const validIds = new Set(questionsList.map((question) => question.id))
    return Object.fromEntries(Object.entries(generated).filter(([id]) => validIds.has(id)))
  }

  const merged = { ...current }
  for (const question of questionsList) {
    if (merged[question.id] === undefined && generated[question.id] !== undefined) {
      merged[question.id] = generated[question.id]
    }
  }
  return merged
}

export function firstUnansweredQuestionIndex(questionsList: FlatQuestion[], answerObject: AnswerObject): number {
  const idx = questionsList.findIndex(
    (question) =>
      isQuestionVisible(question, answerObject, questionsList) &&
      !question.optional &&
      answerObject[question.id] === undefined,
  )
  if (idx >= 0) {
    return idx
  }
  return Math.max(0, questionsList.length - 1)
}

export type ApplyScan26AutofillInput = {
  questionsList: FlatQuestion[]
  currentAnswers: AnswerObject
  source: 'random' | string
  scope: Scan26AutofillScope
}

export type ApplyScan26AutofillResult = {
  answerObject: AnswerObject
  focusQuestionId: string | undefined
  filledCount: number
  warnings: string[]
}

export function applyScan26Autofill(input: ApplyScan26AutofillInput): ApplyScan26AutofillResult {
  const { questionsList, currentAnswers, source, scope } = input
  const warnings: string[] = []

  if (!questionsList.length) {
    return {
      answerObject: currentAnswers,
      focusQuestionId: undefined,
      filledCount: 0,
      warnings: ['Aucune question active dans le questionnaire courant.'],
    }
  }

  let generated: AnswerObject
  if (source === 'random') {
    generated = buildRandomAnswers(questionsList)
  } else {
    const preset = getScan26PresetById(source)
    if (!preset) {
      return {
        answerObject: currentAnswers,
        focusQuestionId: undefined,
        filledCount: 0,
        warnings: [`Preset inconnu : ${source}`],
      }
    }
    generated = buildAnswersFromPreset(questionsList, preset.answers)
    const matched = Object.keys(generated).length
    if (matched === 0) {
      warnings.push(`Le preset « ${preset.label} » ne couvre aucune question du survey courant.`)
    }
  }

  const beforeKeys = new Set(
    questionsList.filter((q) => currentAnswers[q.id] !== undefined).map((q) => q.id),
  )
  const merged = mergeAutofillAnswers(currentAnswers, generated, questionsList, scope)
  const filledCount = questionsList.filter((q) => {
    const had = beforeKeys.has(q.id)
    const has = merged[q.id] !== undefined
    return scope === 'from_zero' ? has : has && !had
  }).length

  const focusIndex = firstUnansweredQuestionIndex(questionsList, merged)
  const focusQuestionId = questionsList[focusIndex]?.id

  return { answerObject: merged, focusQuestionId, filledCount, warnings }
}
